import { Link, useSearchParams } from 'react-router-dom'
import { useAsync } from '../hooks/useAsync'
import { fetchGames, fetchSeasons } from '../services/league'
import SeasonPicker from '../components/SeasonPicker'
import {
  Card,
  ConferenceBadge,
  EmptyState,
  Loading,
  Notice,
  PlayerCell,
  PlayoffBadge,
  Segmented,
  SortableHeaderRow,
  dateSortValue,
  formatDate,
  playerProfilePath,
  useTableSort,
  type SortableColumn,
} from '../components/ui'
import type { GameSummary, GameTypeFilter } from '../types'

/**
 * @remarks
 * `Score` orders by the two scores added together, which is the only reading
 * of a `35–14` cell that does not silently privilege one side of a game the
 * table presents as symmetric. The header carries a tooltip saying so.
 */
const GAME_COLUMNS: Array<SortableColumn<GameSummary>> = [
  {
    key: 'date',
    label: 'Date',
    sort: game => dateSortValue(game.playedAt ?? game.uploadedAt),
  },
  { key: 'season', label: 'Season', sort: game => game.seasonNumber },
  { key: 'conference', label: 'Conf', sortLabel: 'conference', sort: game => game.conference },
  { key: 'home', label: 'Home', sort: game => game.home.displayName },
  {
    key: 'score',
    label: 'Score',
    className: 'num',
    hint: 'Sort by combined points',
    sortLabel: 'combined score',
    sort: game => game.home.score + game.away.score,
  },
  { key: 'away', label: 'Away', sort: game => game.away.displayName },
  { key: 'actions' },
]

function GamesTable({ games }: { games: GameSummary[] }) {
  const sort = useTableSort(games, GAME_COLUMNS)

  return (
    <div className="table-wrap">
      <table className="data">
        <thead>
          <SortableHeaderRow columns={GAME_COLUMNS} sort={sort} />
        </thead>
        <tbody>
          {sort.rows.map(game => (
            <tr key={game.id}>
              <td className="muted">{formatDate(game.playedAt ?? game.uploadedAt)}</td>
              <td className="num">{game.seasonNumber}</td>
              <td>
                <span className="badge-stack">
                  <ConferenceBadge conference={game.conference} />
                  <PlayoffBadge isPlayoff={game.isPlayoff} compact />
                </span>
              </td>
              <td>
                <PlayerCell
                  name={game.home.displayName}
                  discordId={game.home.discordId}
                  avatar={game.home.discordAvatar}
                  team={game.home.teamAbbreviation}
                  to={
                    game.home.leaguePlayerId === null
                      ? undefined
                      : playerProfilePath(game.home.leaguePlayerId)
                  }
                />
              </td>
              <td className="num">
                <strong>
                  {game.home.score}–{game.away.score}
                </strong>
              </td>
              <td>
                <PlayerCell
                  name={game.away.displayName}
                  discordId={game.away.discordId}
                  avatar={game.away.discordAvatar}
                  team={game.away.teamAbbreviation}
                  to={
                    game.away.leaguePlayerId === null
                      ? undefined
                      : playerProfilePath(game.away.leaguePlayerId)
                  }
                />
              </td>
              <td className="num">
                <Link className="btn btn--ghost btn--sm" to={`/games/${game.id}`}>
                  Box score
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * Reads the `season` query parameter.
 *
 * @returns The requested season, `'all'`, or null when the URL asks for nothing
 * usable and the current season should win instead.
 */
function parseSeasonParam(value: string | null): number | 'all' | null {
  if (value === 'all') return 'all'
  const parsed = Number(value)
  return value !== null && Number.isInteger(parsed) && parsed > 0 ? parsed : null
}

/** Reads the `type` query parameter, defaulting to the unfiltered list. */
function parseTypeParam(value: string | null): GameTypeFilter {
  return value === 'regular' || value === 'playoffs' ? value : 'all'
}

const GAME_TYPE_OPTIONS: Array<{ value: GameTypeFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'regular', label: 'Regular season' },
  { value: 'playoffs', label: 'Playoffs' },
]

/**
 * The full results table, filtered by season and optionally to one player.
 *
 * @remarks
 * Only `official` games are listed, and there is no control to see anything
 * else. The other two states are not browsable categories: `void` is a result
 * the league has struck off, and `pending` only ever means an upload whose
 * parsed file was never assigned to two players — a half-finished row with no
 * participants and no score to show, not a game awaiting kickoff. Offering
 * either as a filter invited visitors to go looking for games that, by
 * definition, have nothing to display.
 *
 * Season and player both live in the query string rather than in component
 * state, which is what lets a standings row link to one player's results *for
 * the season being viewed*. Keeping the season in state instead would silently
 * reinterpret every such link as "this player, current season" and show an
 * empty table for any earlier year. The picker replaces the history entry
 * rather than pushing one, so Back still leaves the page instead of walking
 * through every season the visitor tried.
 *
 * The feed link under the subtitle is a plain `<a>`, not a router `Link`.
 * `/rss` is served by nginx ahead of the SPA fallback, so react-router would
 * only try to match it as an app route and land on the 404 page instead of
 * ever leaving the bundle.
 */
export default function GamesList() {
  const [searchParams, setSearchParams] = useSearchParams()
  const playerFilter = searchParams.get('player')
  // Only meaningful alongside a player, which is what the API enforces too.
  const opponentFilter = playerFilter ? searchParams.get('opponent') : null

  const seasons = useAsync(fetchSeasons, [])

  const currentSeason = seasons.data?.find(season => season.isCurrent)
  const seasonValue = parseSeasonParam(searchParams.get('season')) ?? currentSeason?.number

  const gameType = parseTypeParam(searchParams.get('type'))

  const games = useAsync(
    () =>
      fetchGames({
        season: seasonValue,
        status: 'official',
        type: gameType,
        player: playerFilter ? Number(playerFilter) : undefined,
        opponent: opponentFilter ? Number(opponentFilter) : undefined,
        limit: 300,
      }),
    [seasonValue, gameType, playerFilter, opponentFilter]
  )

  function selectType(next: GameTypeFilter) {
    const params = new URLSearchParams(searchParams)
    if (next === 'all') params.delete('type')
    else params.set('type', next)
    setSearchParams(params, { replace: true })
  }

  function selectSeason(next: number | 'all') {
    const params = new URLSearchParams(searchParams)
    params.set('season', String(next))
    setSearchParams(params, { replace: true })
  }

  const clearPlayerQuery = new URLSearchParams(searchParams)
  clearPlayerQuery.delete('player')
  clearPlayerQuery.delete('opponent')

  // Drops the opponent but keeps the player, so a visitor who arrived from a
  // rivalry row can widen to that player's whole card in one click.
  const clearOpponentQuery = new URLSearchParams(searchParams)
  clearOpponentQuery.delete('opponent')

  const seasonScopeLabel =
    seasonValue === undefined || seasonValue === 'all'
      ? 'games'
      : `Season ${seasonValue} games`

  // Named from the results themselves; a player with no games this season has
  // nothing to read a name off, and the notice falls back to the generic wording.
  const nameOf = (leaguePlayerId: string | null): string | null =>
    leaguePlayerId && games.data
      ? (games.data
          .flatMap(game => [game.home, game.away])
          .find(side => side.leaguePlayerId === Number(leaguePlayerId))?.displayName ?? null)
      : null

  const filteredPlayerName = nameOf(playerFilter)
  const filteredOpponentName = nameOf(opponentFilter)

  return (
    <div className="stack">
      <header className="page-header">
        <div>
          <span className="eyebrow">Games</span>
          <h1>Results</h1>
          <p>Every game on the books, newest first.</p>
          <p className="muted" style={{ fontSize: '0.82rem' }}>
            <a
              href="/rss"
              title="Every new result as an RSS feed — paste this address into a feed reader"
              style={{ textDecoration: 'underline' }}
            >
              Subscribe via RSS
            </a>
          </p>
        </div>
        <div className="row">
          <Segmented options={GAME_TYPE_OPTIONS} value={gameType} onChange={selectType} />
          {seasons.data && seasons.data.length > 0 && (
            <SeasonPicker
              seasons={seasons.data}
              value={seasonValue ?? 'all'}
              onChange={selectSeason}
              includeAll
            />
          )}
        </div>
      </header>

      {games.error && <Notice tone="error">{games.error}</Notice>}

      {playerFilter && (
        <Notice>
          {opponentFilter ? (
            <>
              Showing every meeting between {filteredPlayerName ?? 'one player'} and{' '}
              {filteredOpponentName ?? 'their opponent'}.{' '}
              <Link
                to={`/games?${clearOpponentQuery}`}
                style={{ textDecoration: 'underline' }}
              >
                All games for {filteredPlayerName ?? 'this player'}
              </Link>
            </>
          ) : (
            <>
              Showing {seasonScopeLabel} for {filteredPlayerName ?? 'one player'}.{' '}
              <Link
                to={`/games?${clearPlayerQuery}`}
                style={{ textDecoration: 'underline' }}
              >
                Clear filter
              </Link>
            </>
          )}
        </Notice>
      )}

      <Card title={`${games.data?.length ?? 0} games`} flush>
        {games.loading ? (
          <div style={{ padding: 24 }}>
            <Loading rows={6} />
          </div>
        ) : games.data && games.data.length > 0 ? (
          <GamesTable games={games.data} />
        ) : (
          <EmptyState title="No games here yet">
            {gameType === 'playoffs'
              ? 'No playoff games are on file for this filter. Early-season playoff games were never imported — their brackets live on the playoffs page.'
              : opponentFilter
                ? 'These two have never met in the season shown.'
                : playerFilter
                  ? 'This player has no results in the season shown.'
                  : 'Try a different season.'}
          </EmptyState>
        )}
      </Card>
    </div>
  )
}
